import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { consts, Routes_DE as Routes } from '@allesauto/utils/consts';
import { getDaysForLocale, isSpecialCharacter } from '@allesauto/utils/funcs';
import { Chat16, Link16, Location16, Phone16 } from '@carbon/icons-react';
import { Pen16 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import ContactDealerForm from '@/components/forms/contactDealerForm/ContactDealerForm';
import LocationMap from '@/components/LocationMap/LocationMap';

import { WL_INTEGRATION_SLUG_AUTO_HEMPEL } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';

export interface CustomerDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
}

export interface DealerInfoProps {
  dealerDetails: SchemaTypes.Dealer;
  openingHours: SchemaTypes.OpeningHours[];
  vehicleId: string;
  carModel: string;
  carMake: string;
  bgColor?: string;
  customerDetails?: CustomerDetails;
  hasMap?: boolean;
  hasContactDealer?: boolean;
  paymentType: SchemaTypes.PaymentType | null;
  financeCalculationResult?: SchemaTypes.FinanceCalculationResultInput;
  editDealer?: () => void;
  vehiclePrice?: number;
}

const DealerInfo: FC<React.PropsWithChildren<DealerInfoProps>> = ({
  openingHours,
  dealerDetails,
  vehicleId,
  carMake,
  carModel,
  customerDetails,
  bgColor = 'bg-grey10 monochrome:bg-grey10',
  hasMap = true,
  hasContactDealer = true,
  editDealer,
  paymentType,
  financeCalculationResult,
  vehiclePrice = 0,
}) => {
  const { name, website, contactPerson, location, id } = dealerDetails || {};
  const { firstName, lastName, phone } = contactPerson || {};
  const { additionalAddress, city, geoCoordinates, houseNumber, street, zip } =
    location || {};
  const { lat, lon } = geoCoordinates || {};
  const { GOOGLE_MAPS_PLACE } = consts.PDP.DEALER_INFO;
  const intl = useIntl();
  const dispatch = useDispatch();
  const router = useRouter();

  const whiteLabel = useWhiteLabel();

  const isAutoHempelHeader =
    whiteLabel?.merchantOrigin === WL_INTEGRATION_SLUG_AUTO_HEMPEL;

  const weekdays = useMemo(() => {
    const keyDays = getDaysForLocale('en-US', 'long'); // keys for the days are always in english
    const localeDays = getDaysForLocale(intl.locale, 'long'); // translations are in the current locale

    // map keys and translations to use the dictionary later
    return keyDays.reduce<Record<string, string>>((days, dayKey, i) => {
      days[dayKey.toUpperCase()] = localeDays[i];

      return days;
    }, {});
  }, [intl]);

  const handleBackToSearch = () => {
    dispatch(closeOverlay());

    return router.push(Routes.CARS.ALL);
  };

  const contactProps = {
    children: (
      <ContactDealerForm
        dealerId={id}
        vehicleSku={vehicleId}
        darkBg={false}
        carModel={carModel}
        carManufacturer={carMake}
        dealerName={name || ''}
        onBackToSearchClick={handleBackToSearch}
        contactPerson={{
          firstName: firstName || '',
          lastName: lastName || '',
          phone: phone || '',
        }}
        paymentType={paymentType}
        financeCalculationResult={financeCalculationResult}
        customerDetails={customerDetails}
        vehiclePrice={vehiclePrice}
      />
    ),
    title: translate(intl, 'ui.FORMS.CONTACT_DEALER.TITLE'),
  };

  const googleMapLink = `${GOOGLE_MAPS_PLACE}${lat},${lon}`;

  const className = {
    wrapperClass: hasMap
      ? clsx(
          `col-end-25 col-start-1 grid grid-cols-24 main-wrapper relative sm:pb-0 md:bg-purple80 ${
            isAutoHempelHeader
              ? 'md:monochrome:bg-white'
              : 'md:monochrome:bg-grey90'
          }  md:pb-60px`,
          bgColor
        )
      : 'md:m-60px',
    whiteBackgroundClass: clsx(
      'absolute h-60px left-0 top-0 w-full z-[5] sm:hidden md:block',
      bgColor,
      isAutoHempelHeader && 'monochrome:bg-white'
    ),
    titleContent: clsx(
      'flex flex-row justify-between md:hidden',
      hasMap ? 'md:pt-80px pb-30px' : 'md:pt-60px pb-20px'
    ),
    title: clsx(
      'font-headline text-headline3 text-purple80 monochrome:text-grey90',
      hasMap && 'sm:pt-60px'
    ),
    schedule: clsx(
      'mb-5px sm:table-row md:grid',
      hasMap ? 'grid-cols-2' : 'grid-cols-3'
    ),
    scheduleTitle: clsx(
      'font-headline text-headline4 text-purple80 monochrome:text-grey90',
      hasMap ? 'pb-30px pt-60px' : 'pb-20px pt-40px '
    ),
    openingHours: clsx(
      'text-grey100 sm:col-end-23 sm:col-start-3 md:col-end-6',
      hasMap ? 'md:col-start-2' : 'md:ml-60px md:col-start-1'
    ),
    address: clsx(
      'sm:col-end-23 sm:col-start-3 md:col-end-11',
      hasMap ? 'md:col-start-2' : 'md:ml-60px md:col-start-1'
    ),
    website: clsx(
      'pdp-dealer-website text-grey100 sm:hidden md:block md:col-end-6',
      hasMap ? 'md:col-start-2' : 'ml-60px md:col-start-1'
    ),
    editLink: clsx(
      'cursor-pointer flex justify-end',
      !hasMap && 'sm:mt-20px sm:mb-10px'
    ),
    greenAreaWrapper: `bg-green60 flex flex-col green-area-wrapper pb-60px z-20 sm:col-end-25 sm:col-start-1 sm:mt-15px md:col-end-23 md:col-start-3 md:mt-0 lg:col-end-13 lg:col-start-3 ${
      isAutoHempelHeader ? 'monochrome:bg-[#E0E0E0]' : 'monochrome:bg-grey40'
    }`,
    googleMapWrapper:
      'lg:col-end-24 lg:col-start-14 md:col-end-23 md:col-start-3 flex flex-1 google-maps-wrapper z-10 mt-60px lg:pt-60px sm:hidden md:block xl:col-end-23 h-[400px] lg:h-auto pt-0;',
  };

  const editLink = (
    <div className={className.editLink}>
      <TextLink
        icon={<Pen16 />}
        text={translate(intl, 'ui.USER_ACCOUNT_DETAILS.GARAGE.LINK_EDIT_TEXT')}
        onClick={editDealer}
        id="link--edit-dealer"
      />
    </div>
  );

  const title = (
    <h3 className={className.title} id="label--dealer-name">
      {name && !isSpecialCharacter(name) && name}
    </h3>
  );

  return (
    <div className={className.wrapperClass} data-style-for-print="dealer-info">
      <h2 className="sr-only">
        {translate(intl, 'ui.PDP.DEALER_INFO.OPENING_HOURS_AND_CONTACT')}
      </h2>
      {hasMap && <div className={className.whiteBackgroundClass}></div>}
      <div className={className.greenAreaWrapper}>
        <div className="grid sm:grid-cols-24 md:grid-cols-11">
          <div className={className.address}>
            <div className={className.titleContent}>
              {title}
              {!hasMap && editLink}
            </div>
            <div className="sm:hidden md:block">
              {!hasMap && (
                <div
                  className={'mt-20px mb-10px flex justify-end'}
                  onClick={editDealer}
                  onKeyPress={editDealer}
                  id="icon--edit-dealer"
                >
                  <div className="bg-white flex h-30px items-center justify-center rounded-circle w-30px">
                    <Pen16 className="fill-purple80 monochrome:fill-black"></Pen16>
                  </div>
                </div>
              )}
              {title}
            </div>
            <p id="label--address">
              {street && !isSpecialCharacter(street) && street}{' '}
              {houseNumber && !isSpecialCharacter(houseNumber) && houseNumber}
            </p>
            <p id="label--additional-address">
              {additionalAddress &&
                !isSpecialCharacter(additionalAddress) &&
                additionalAddress}
            </p>
            <p id="label--zipCode-and-city">
              {zip && !isSpecialCharacter(zip) && zip}{' '}
              {city && !isSpecialCharacter(city) && city}
            </p>
            <p className={className.scheduleTitle}>
              {translate(intl, 'ui.PDP.DEALER_INFO.OPENING_HOURS_AND_CONTACT')}
            </p>
          </div>

          <div className={className.openingHours} id="label--openingHours">
            {openingHours
              ?.filter((i) => i.day)
              .map((item, index) => {
                return (
                  <p key={item.day} className={className.schedule}>
                    <span
                      className="pb-5px sm:pr-20px sm:table-cell xl:pr-25px"
                      id={`label--weekday-${index}`}
                    >
                      {weekdays[item.day ?? '']}
                    </span>
                    <span
                      className="whitespace-nowrap sm:pb-[9px] sm:table-cell md:pb-5px"
                      id={`label--oppeningHours-${index}`}
                    >
                      {item.isActive
                        ? translate(
                            intl,
                            'ui.PDP.DEALER_INFO.OPENING_HOURS_RANGE',
                            { from: item.from ?? '', to: item.to ?? '' }
                          )
                        : translate(
                            intl,
                            'ui.PDP.DEALER_INFO.OPENING_HOURS_CLOSED'
                          )}
                    </span>
                  </p>
                );
              })}
          </div>

          <div className="text-grey100 sm:col-end-23 sm:col-start-3 sm:pb-5px md:col-end-11 md:col-start-7 md:pb-30px">
            <p
              className="text-p3 sm:pt-30px md:block md:pt-0"
              id={`label--contact-person`}
            >
              {translate(
                intl,
                whiteLabel
                  ? 'ui.PDP.DEALER_INFO.CONTACT_PERSON_WL'
                  : 'ui.PDP.DEALER_INFO.CONTACT_PERSON'
              )}
            </p>
            <p
              className="font-weight-bold sm:pb-30px sm:pt-5px md:pb-10px"
              id={`label--lastName-and-FirstName`}
            >
              {lastName && !isSpecialCharacter(lastName) && lastName}{' '}
              {firstName && !isSpecialCharacter(firstName) && firstName}
            </p>
            <p className="flex-row sm:hidden md:flex">
              <Phone16 className="mt-3px" />
              <a
                href={`tel:${phone}`}
                className="no-underline pl-5px text-black hover:text-black"
                id={`link--phoneNumber`}
              >
                {phone && !isSpecialCharacter(phone) && phone}
              </a>
            </p>

            <div className={className.website}>
              {website && (
                <TextLink
                  text={website}
                  icon={<Link16 />}
                  href={`http://${website}`}
                  target="_blank"
                  id="link--website"
                />
              )}
            </div>
            {hasContactDealer && (
              <div className="mt-10px pdp-dealer-email text-grey100 sm:hidden md:block md:col-end-11 md:col-start-7">
                <TextLink
                  text={translate(intl, 'ui.PDP.DEALER_INFO.CONTACT_EMAIL')}
                  icon={<Chat16 />}
                  onClick={() => {
                    // dataLayer push
                    pushAnalyticsEvent({
                      event: 'contact',
                      form_process: 'contact_formStart',
                      product_name: `${carMake} ${carModel}`,
                      product_id: vehicleId,
                    });

                    dispatch(openOverlay(contactProps));
                  }}
                  id={`link--email`}
                />
              </div>
            )}
          </div>
          <div className="col-end-25 col-start-3 flex-col sm:flex md:hidden">
            <div className="flex flex-row hide-scrollbar overflow-x-scroll swipper-wrapper-mobile">
              {phone && (
                <TextLink
                  text="Anrufen"
                  icon={<Phone16 className="h-20px w-20px" />}
                  href={`tel:${phone}`}
                  id={`link--phoneNumber`}
                  mobileBlockVariant={true}
                  customClassName="rounded-[0px]"
                />
              )}
              {hasContactDealer && (
                <TextLink
                  text="Kontaktieren"
                  icon={<Chat16 className="h-20px w-20px" />}
                  onClick={() => {
                    // dataLayer push
                    pushAnalyticsEvent({
                      event: 'contact',
                      form_process: 'contact_formStart',
                      product_name: `${carMake} ${carModel}`,
                      product_id: vehicleId,
                    });

                    dispatch(openOverlay(contactProps));
                  }}
                  id={`link--contactDealer`}
                  mobileBlockVariant={true}
                  customClassName="rounded-[0px]"
                />
              )}
              {hasMap && (
                <TextLink
                  text="Anfahrt"
                  icon={<Location16 className="h-20px w-20px" />}
                  href={googleMapLink}
                  target="_blank"
                  id={`link--googleMap`}
                  mobileBlockVariant={true}
                  customClassName="rounded-[0px]"
                />
              )}
              {website && (
                <TextLink
                  text="Website"
                  target="_blank"
                  icon={<Link16 className="h-20px w-20px" />}
                  href={website}
                  id={`link--website`}
                  mobileBlockVariant={true}
                  customClassName="rounded-[0px]"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {hasMap && (
        <LocationMap
          lat={lat}
          lng={lon}
          width="100%"
          height="100%"
          className={className.googleMapWrapper}
          isDarkBg
        />
      )}
    </div>
  );
};

export default DealerInfo;
